import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getTimetables,
  getDailyTimetables,
  getDetailTimetable,
  updateTimetable,
} from '../api/campusClasses3.api';
import {
  ITimetable,
  IGetTimetables,
  IDailyTimetable,
  IGetDailyTimetables,
  IDetailTimetable,
  IGetTimetable,
  ITimetableReq,
} from '../models/campusClasses3.model';

interface ITimetableState {
  timetable?: IDetailTimetable;
  timetables?: ITimetable[];
  dailyTimetables?: IDailyTimetable[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ITimetableState = {
  timetable: undefined,
  timetables: [],
  dailyTimetables: [],
  isLoading: false,
};

const classTimetableSlicer = createSlice({
  name: 'classTimetable',
  initialState,
  reducers: {
    getTimetableStart: (state: ITimetableState) => {
      state.timetable = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getTimetableSuccess: (
      state: ITimetableState,
      action: PayloadAction<IGetTimetable>,
    ) => {
      state.timetable = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getTimetableFailure: (
      state: ITimetableState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.timetable = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getTimetablesStart: (state: ITimetableState) => {
      state.timetables = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getTimetablesSuccess: (
      state: ITimetableState,
      action: PayloadAction<IGetTimetables>,
    ) => {
      state.timetables = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getTimetablesFailure: (
      state: ITimetableState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.timetables = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getDailyTimetablesStart: (state: ITimetableState) => {
      state.dailyTimetables = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getDailyTimetablesSuccess: (
      state: ITimetableState,
      action: PayloadAction<IGetDailyTimetables>,
    ) => {
      state.dailyTimetables = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getDailyTimetablesFailure: (
      state: ITimetableState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.dailyTimetables = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ITimetableState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ITimetableState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateSuccess: (state: ITimetableState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getTimetableStart,
  getTimetableSuccess,
  getTimetableFailure,
  getTimetablesStart,
  getTimetablesSuccess,
  getTimetablesFailure,
  getDailyTimetablesStart,
  getDailyTimetablesSuccess,
  getDailyTimetablesFailure,
  requestStart,
  requestFailure,
  updateSuccess,
} = classTimetableSlicer.actions;

export default classTimetableSlicer.reducer;

export const doGetDetailTimetable =
  (timetableCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getTimetableStart());
      const response = await getDetailTimetable(timetableCode);
      dispatch(getTimetableSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getTimetableFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetClassTimetables =
  (classCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getTimetablesStart());
      const response = await getTimetables(classCode);
      dispatch(getTimetablesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getTimetablesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetDailyTimetables =
  (classCode: string, timetableCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getDailyTimetablesStart());
      const response = await getDailyTimetables(classCode, timetableCode);
      dispatch(getDailyTimetablesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getTimetablesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

// export const doUpdateRollbook = (classCode: string, startDate: string, data: IRollbookReq): any => async (dispatch: any) => {
//   try {
//     dispatch(requestStart());
//     const response = await updateRollbook(classCode, startDate, data);
//     dispatch(updateSuccess());
//     return response;
//   } catch (e: any) {
//     dispatch(requestFailure(e.response?.data?.data || e.message));
//     return e.response;
//   }
// };

export const doUpdateTimetable =
  (timetableCode: string, data: ITimetableReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateTimetable(timetableCode, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

// export const doCreateClassTimetable = (classCode: string, data: ITimetableReq): any => async (dispatch: any) => {
//   try {
//     dispatch(requestStart());
//     const response = await createTimetable(classCode, data);
//     dispatch(createSuccess());
//     return response;
//   } catch (e: any) {
//     dispatch(requestFailure(e.response?.data?.data || e.message));
//     return e.response;
//   }
// };

// export const doUpdateTimetable = (id: string, data: ITimetableReq): any => async (dispatch: any) => {
//   try {
//     dispatch(requestStart());
//     const response = await updateTimetable(id, data);
//     dispatch(updateSuccess());
//     return response;
//   } catch (e: any) {
//     dispatch(requestFailure(e.response?.data?.data || e.message));
//     return e.response;
//   }
// };

// export const doDeleteTimetable = (id: string): any => async (dispatch: any) => {
//   try {
//     dispatch(requestStart());
//     const response = await deleteTimetable(id);
//     dispatch(deleteSuccess());
//     return response;
//   } catch (e: any) {
//     dispatch(requestFailure(e.response?.data?.data || e.message));
//     return e.response;
//   }
// };
